<template>
  <div class="tester-test-view">
    <v-row class="overflow-y-auto">
      <v-col>
        <h4 class="mb-1">{{ $t("tests.room") }}</h4>
        <div class="body1 black20">
          {{ roomType }}
        </div>
        <div
          v-if="
            illumination_tests.find(
              (test) => test.illumination_test_id === selected_test_id
            )
          "
        >
          <h4 class="mt-4 mb-1">
            {{ $t("tests.illumination.measurement_comment") }}
          </h4>
          <div class="body1 black20">
            {{
              measurement_points_comment !== "" &&
              measurement_points_comment !== " "
                ? `${measurement_points_comment}`
                : " - "
            }}
          </div>
        </div>

        <v-divider class="mt-4"></v-divider>

        <div class="caption mt-6">{{ $t("tests.select") }}</div>
        <niko-dropdown
          class="mt-2"
          :items="allTests"
          item-text="name"
          item-value="id"
          :value="selected_test_id"
          @onSelect="(value) => setSelectedTestId(value)"
        ></niko-dropdown>

        <h4 class="mt-4">{{ $t("tests.preconditions") }}</h4>
        <div class="body1 black20 mt-1">
          {{ testRequirements }}
        </div>
        <div
          v-if="
            illumination_tests.find(
              (test) => test.illumination_test_id === selected_test_id
            )
          "
        >
          <div class="caption mt-4">
            {{ $t("tests.illumination.measurement_conditions.label") }}
          </div>

          <niko-textarea
            :rows="1"
            :value="
              illumination_tests.find(
                (test) => test.illumination_test_id === selected_test_id
              ).measurement_conditions
            "
            @onChange="(value) => measurementConditionsInputChanged(value)"
            :placeholder="
              $t('tests.illumination.measurement_conditions.placeholder')
            "
          />

          <points-measured-card />

          <h4>{{ $t("tests.illumination.points") }}</h4>

          <v-list-item-group>
            <div
              v-for="point of measurement_points.filter(
                (point) => point.is_active
              )"
              :key="point.measurement_point_id"
            >
              <v-list-item
                @click="
                  setSelectedMeasurementPointId(point.measurement_point_id)
                "
                outlined
                style="height: 40px"
                class="d-flex justify-space-between"
              >
                <div>{{ point.location }}</div>
                <div class="overline">
                  {{
                    illumination_tests
                      .find(
                        (test) => test.illumination_test_id === selected_test_id
                      )
                      .illumination_test_results.find(
                        (result) =>
                          result.measurement_point_id ===
                          point.measurement_point_id
                      )
                      ? `${
                          illumination_tests
                            .find(
                              (test) =>
                                test.illumination_test_id === selected_test_id
                            )
                            .illumination_test_results.find(
                              (result) =>
                                result.measurement_point_id ===
                                point.measurement_point_id
                            ).measured_lux
                        } LUX`
                      : null
                  }}
                </div>
              </v-list-item>
              <v-divider></v-divider>
            </div>
          </v-list-item-group>
          <guidance-button page="testing.illumination_test" />
        </div>
        <div class="mt-6" v-else>
          <tester-functional-test-results
            :test="{
              ...functional_tests.find(
                (test) => test.functional_test_id === selected_test_id
              ),
            }"
          />
        </div>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import { mapActions, mapMutations, mapState } from "vuex";
import NikoDropdown from "@/components/UI/NikoDropdown";
import NikoTextarea from "@/components/UI/NikoTextarea";
import GuidanceButton from "@/components/guidance/GuidanceButton";
import PointsMeasuredCard from "./PointsMeasuredCard";
import TesterFunctionalTestResults from "./TesterFunctionalTestResults";
import { eventBus } from "@/components/EventBus";
import i18n from "../../i18n";

export default {
  name: "tester-test-view",
  components: {
    NikoDropdown,
    PointsMeasuredCard,
    TesterFunctionalTestResults,
    NikoTextarea,
    GuidanceButton,
  },
  computed: {
    ...mapState("roomStore", [
      "room_type",
      "measurement_points_comment",
      "measurement_points",
      "illumination_tests",
      "functional_tests",
      "selected_test_id",
    ]),
    roomType() {
      return /^\d/.test(this.room_type)
        ? this.room_type.replace(/\d+.\d+.\d+/g, "")
        : i18n.t(`room.type.${this.room_type}`);
    },
    allTests() {
      const formatTest = (test, id, done) => ({
        ...test,
        id,
        label: `
          <div>${test.name}</div>
            <div
              class="${done ? "text-green" : "text-red"}"
              style="font-size: 10px"
            >
              ${done ? this.$t("tests.done") : this.$t("tests.not_done")}
            </div>
          `,
      });

      return [
        ...this.illumination_tests,
        ...this.functional_tests.filter(
          (test) =>
            test.daylight_control_enabled || test.motion_detectors_enabled
        ),
      ].map((test) => {
        let id;
        let done;

        if (test.illumination_test_id) {
          id = test.illumination_test_id;
          done =
            test.illumination_test_results.length ===
            this.measurement_points.filter((point) => point.is_active).length;
        }
        if (test.functional_test_id) {
          id = test.functional_test_id;
          done = this.isFunctionalTestDone(test);
        }

        return formatTest(test, id, done);
      });
    },
    testRequirements() {
      return this.allTests.find((test) => test.id === this.selected_test_id)
        .test_requirements !== " " &&
        this.allTests.find((test) => test.id === this.selected_test_id)
          .test_requirements !== ""
        ? `${
            this.allTests.find((test) => test.id === this.selected_test_id)
              .test_requirements
          }`
        : "-";
    },
  },
  methods: {
    ...mapMutations("roomStore", [
      "setSelectedTestId",
      "setSelectedMeasurementPointId",
    ]),
    ...mapActions("projectStore", ["setMeasurementConditions"]),
    async measurementConditionsInputChanged(value) {
      eventBus.$emit("tester-data-submitting", true);
      const id = this.selected_test_id;
      await this.setMeasurementConditions({
        illumination_test_id: id,
        measurement_conditions: value.length > 0 ? value : null,
      });
      eventBus.$emit("tester-data-submitting", false);
    },
    isFunctionalTestDone(test) {
      const requiredResults =
        (test.daylight_control_enabled ? 1 : 0) +
        (test.daylight_zones_enabled ? 2 : 0) +
        (test.motion_detectors_enabled ? 3 : 0);
      const enoughResults =
        test.functional_test_results.length === requiredResults;
      const allResultsValid = test.functional_test_results.every(
        (result) => result.answer === true || result.answer === false
      );
      const actualNumberOfZonesSpecified =
        test.actual_number_of_zones !== null &&
        test.actual_number_of_zones !== undefined;
      return (
        enoughResults &&
        allResultsValid &&
        (actualNumberOfZonesSpecified || !test.daylight_zones_enabled)
      );
    },
  },
};
</script>
<style lang="scss">
.tester-test-view {
  ::-webkit-scrollbar {
    background-color: #202324;
    color: #aba499;
    width: 8px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: #454a4d;
  }
  ::-webkit-scrollbar-thumb:hover {
    background-color: #575e62;
  }
  ::-webkit-scrollbar-thumb:active {
    background-color: #484e51;
  }
  ::-webkit-scrollbar-corner {
    background-color: #181a1b;
  }
}
</style>
